import { io } from 'socket.io-client';

// Same-origin in production (Caddy proxies /api and /socket.io); override for local dev
const API_BASE = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'aldamen_admin_token';

let currentUser = null;
let authReady = false;
const authListeners = new Set();

let socket = null;
// key → { refetch, handlers: {event: fn} } — refetched on every (re)connect
const subscriptions = new Map();
let subSeq = 0;

class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) throw new ApiError(res.status, data?.error || res.statusText);
  return data;
}

// ---------------- auth ----------------

function notifyAuth() {
  for (const cb of authListeners) cb(currentUser);
}

function setUser(user) {
  currentUser = user;
  authReady = true;
  if (user) connectSocket();
  else disconnectSocket();
  notifyAuth();
}

async function restoreSession() {
  const token = getToken();
  if (!token) return setUser(null);
  try {
    const { admin } = await request('/api/auth/me');
    setUser({ uid: String(admin.id), email: admin.email, name: admin.name, role: admin.role });
  } catch {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setUser(null);
  }
}

export function onAuthStateChanged(cb) {
  authListeners.add(cb);
  if (authReady) cb(currentUser);
  else if (authListeners.size === 1) restoreSession();
  return () => authListeners.delete(cb);
}

export async function signInWithEmailAndPassword(email, password) {
  const { token, admin } = await request('/api/auth/login', { method: 'POST', body: { email, password } });
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
  setUser({ uid: String(admin.id), email: admin.email, name: admin.name, role: admin.role });
}

export function signOut() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  setUser(null);
}

// ---------------- realtime plumbing ----------------

function connectSocket() {
  if (socket) return;
  socket = io(API_BASE || undefined, { auth: { token: getToken() } });
  socket.on('connect', () => {
    for (const sub of subscriptions.values()) sub.refetch();
  });
  socket.onAny((event, payload) => {
    for (const sub of subscriptions.values()) sub.handlers[event]?.(payload);
  });
}

function disconnectSocket() {
  if (!socket) return;
  socket.disconnect();
  socket = null;
}

function addSubscription({ refetch, handlers = {} }) {
  const key = ++subSeq;
  subscriptions.set(key, { refetch, handlers });
  refetch();
  return () => subscriptions.delete(key);
}

// ---------------- subscriptions (mirror the old onValue call sites) ----------------

export function subscribeAdmins(cb, onError) {
  return addSubscription({
    refetch: () => request('/api/admins').then(cb).catch(() => onError?.()),
  });
}

export function subscribeLeaders(cb, onError) {
  let byId = {};
  const emit = () =>
    cb(Object.values(byId).sort((a, b) => a.name.localeCompare(b.name)));
  return addSubscription({
    refetch: () =>
      request('/api/leaders')
        .then((list) => {
          byId = Object.fromEntries(list.map((l) => [l.id, l]));
          emit();
        })
        .catch(() => onError?.()),
    handlers: {
      'leader:updated': (leader) => {
        byId[leader.id] = leader;
        emit();
      },
    },
  });
}

export function subscribeLeader(id, cb, onError) {
  return addSubscription({
    refetch: () => request(`/api/leaders/${id}`).then(cb).catch(() => onError?.()),
    handlers: {
      'leader:updated': (leader) => {
        if (leader.id === id) cb(leader);
      },
    },
  });
}

export function subscribeLeaderTransactions(id, cb, onError) {
  const refetch = () =>
    request(`/api/leaders/${id}/transactions`).then(cb).catch(() => onError?.());
  return addSubscription({
    refetch,
    handlers: {
      'tx:changed': ({ leaderId }) => {
        if (leaderId === id) refetch();
      },
    },
  });
}

// ---------------- mutations ----------------

export function createTransaction(leaderId, { type, amount, depositorName }) {
  return request(`/api/leaders/${leaderId}/transactions`, {
    method: 'POST',
    body: { type, amount, depositorName },
  });
}

export function deleteTransaction(txId) {
  return request(`/api/transactions/${txId}`, { method: 'DELETE' });
}

export function deleteDepositor(leaderId, depositorId) {
  return request(`/api/leaders/${leaderId}/depositors/${depositorId}`, { method: 'DELETE' });
}

export function createLeader(name, password) {
  return request('/api/leaders', { method: 'POST', body: { name, password } });
}
